import { useCallback, useEffect, useRef, useState } from "react";
import { buildReview, type Review } from "../core/review";
import type { CoachMoveCtx } from "../core/coachPrompt";
import type { ChessEngine } from "../engine/types";
import { CLASS_GLYPH, CLASS_TR } from "../core/classify";
import { Board } from "./components/Board";
import { EvalBar } from "./components/EvalBar";
import { MoveList } from "./components/MoveList";
import { SAMPLES } from "./sampleGames";

type Phase = "idle" | "working" | "done" | "error";

/** Tarayıcı, Groq'a doğrudan gitmez; /api/coach fonksiyonu üzerinden gider. */
async function apiCommentate(ctx: CoachMoveCtx): Promise<string | null> {
  try {
    const r = await fetch("/api/coach", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ctx, lang: "tr" }),
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { text?: unknown };
    return typeof j.text === "string" && j.text.trim() ? j.text : null;
  } catch {
    return null;
  }
}

export function App() {
  const [pgn, setPgn] = useState(SAMPLES[0].pgn);
  const [sampleIdx, setSampleIdx] = useState(0);
  const [depth, setDepth] = useState(14);
  const [useCoach, setUseCoach] = useState(true);
  const [phase, setPhase] = useState<Phase>("idle");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [review, setReview] = useState<Review | null>(null);
  const [ply, setPly] = useState(-1);
  const engineRef = useRef<ChessEngine | null>(null);

  const run = useCallback(async () => {
    if (!pgn.trim()) return;
    setPhase("working");
    setError("");
    setStatus("Motor başlatılıyor…");
    try {
      if (!engineRef.current) {
        const { createBrowserEngine } = await import("../engine/browser");
        engineRef.current = createBrowserEngine();
      }
      const r = await buildReview(pgn, engineRef.current, {
        depth,
        lang: "tr",
        coachModel: useCoach ? "api" : null,
        commentate: useCoach ? apiCommentate : undefined,
        onProgress: (stage, done, total) =>
          setStatus(
            stage === "analyze"
              ? `Stockfish analizi: ${done}/${total} pozisyon`
              : `AI coach yorumu: ${done}/${total} hamle`,
          ),
      });
      setReview(r);
      setPly(r.summary.noteworthyPlies[0] ?? r.moves.length - 1);
      setPhase("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase("error");
    }
  }, [pgn, depth, useCoach]);

  const lastPly = (review?.moves.length ?? 1) - 1;
  const go = useCallback(
    (target: number) => {
      const clamped = Math.max(-1, Math.min(lastPly, target));
      setPly(clamped);
    },
    [lastPly],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") go(ply - 1);
      if (e.key === "ArrowRight") go(ply + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, ply]);

  const fen = review ? (ply < 0 ? review.initialFen : review.moves[ply].fenAfter) : "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";
  const wpWhite = review ? (ply < 0 ? 0.5 : review.moves[ply].winProbAfterWhite) : 0.5;
  const selMove = review && ply >= 0 ? review.moves[ply] : null;

  return (
    <div className="app">
      <header className="topbar">
        <h1>
          ♟ Chessign <span className="tag">Oyun İncelemesi</span>
        </h1>
      </header>

      {!review && (
        <main className="setup">
          <div className="card">
            <h2>PGN ile incele</h2>
            <div className="row">
              <label>
                Örnek oyun:{" "}
                <select
                  value={sampleIdx}
                  onChange={(e) => {
                    const i = Number(e.target.value);
                    setSampleIdx(i);
                    setPgn(SAMPLES[i].pgn);
                  }}
                >
                  {SAMPLES.map((s, i) => (
                    <option key={s.name} value={i}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Derinlik:{" "}
                <select value={depth} onChange={(e) => setDepth(Number(e.target.value))}>
                  <option value={12}>12 (hızlı)</option>
                  <option value={14}>14 (dengeli)</option>
                  <option value={16}>16 (derin)</option>
                </select>
              </label>
              <label className="chk">
                <input type="checkbox" checked={useCoach} onChange={(e) => setUseCoach(e.target.checked)} /> AI coach
                yorumları
              </label>
            </div>
            <textarea value={pgn} onChange={(e) => setPgn(e.target.value)} rows={12} spellCheck={false} />
            <button className="primary" onClick={run} disabled={phase === "working"}>
              {phase === "working" ? "İnceleniyor…" : "İncelemeyi Başlat"}
            </button>
            {phase === "working" && <p className="status">{status}</p>}
            {phase === "error" && <p className="error">Hata: {error}</p>}
            <p className="hint">
              Analiz tarayıcında çalışan Stockfish ile yapılır (sunucuya oyun verisi gönderilmez); koç yorumları için
              yalnızca ilgili hamlenin özet verisi /api/coach üzerinden modele iletilir.
            </p>
          </div>
        </main>
      )}

      {review && (
        <main className="review">
          <section className="boardwrap">
            <EvalBar wpWhite={wpWhite} />
            <div>
              <Board fen={fen} lastMoveUci={selMove?.uci} />
              <div className="nav">
                <button onClick={() => go(-1)}>⏮</button>
                <button onClick={() => go(ply - 1)}>◀</button>
                <button onClick={() => go(ply + 1)}>▶</button>
                <button onClick={() => go(lastPly)}>⏭</button>
              </div>
            </div>
          </section>

          <aside className="sidepanel">
            <div className="card summary">
              <div className="players">
                <strong>{review.meta.white}</strong> <span className="acc">%{review.summary.accuracyWhite}</span>
                <em>{review.meta.result}</em>
                <strong>{review.meta.black}</strong> <span className="acc">%{review.summary.accuracyBlack}</span>
              </div>
              <div className="counts">
                {Object.entries(review.summary.counts).map(([k, v]) => (
                  <span key={k} className={`chip cls-${k}`}>
                    {CLASS_GLYPH[k as keyof typeof CLASS_GLYPH]} {CLASS_TR[k as keyof typeof CLASS_TR]} × {v}
                  </span>
                ))}
              </div>
            </div>

            {selMove && (
              <div className={`card coach cls-${selMove.classification}`}>
                <h3>
                  {selMove.moveNumber}
                  {selMove.color === "w" ? "." : "…"} {selMove.san}{" "}
                  <span className="badge">
                    {CLASS_GLYPH[selMove.classification]} {CLASS_TR[selMove.classification]}
                  </span>
                </h3>
                <p className="coachline">
                  {selMove.commentary ??
                    (selMove.classification === "excellent" || selMove.classification === "good"
                      ? "Standart, sağlam bir hamle."
                      : selMove.onlyLegalMove
                        ? "Bu konumda oynanabilecek tek yasal hamleydi."
                        : `En iyi hamle: ${selMove.bestMoveSan}`)}
                </p>
                {selMove.bestLineSan.length > 0 && selMove.classification !== "best" && (
                  <p className="bestline">En iyi varyant: {selMove.bestLineSan.join(" ")}</p>
                )}
              </div>
            )}

            <div className="card moves-card">
              <MoveList
                moves={review.moves}
                sel={ply}
                onSelect={go}
              />
            </div>

            <button
              className="ghost"
              onClick={() => {
                setReview(null);
                setPly(-1);
                setPhase("idle");
              }}
            >
              ← Yeni inceleme
            </button>
          </aside>
        </main>
      )}
    </div>
  );
}
